import { ref } from "vue";
import router from "@/router/index";
const API_URL = process.env.VUE_APP_ROOT_API;
import { clubUUID, userUUID, loggedIn } from "@/shared/state";
import { useUser } from "@/composables/auth/useUser";

import { useClub } from "@/composables/club/useClub";

import Swal from "sweetalert2";
import i18n from "@/i18n/index";
const Toast = Swal.mixin({
  toast: true,
  position: "top-right",
  showConfirmButton: false,
  timer: 12000,
  timerProgressBar: true,
});

export function useLogin() {
  const { initialValuesClub } = useClub();
  const { hasAdminRole, hasSuperAdminRole } = useUser();
  const error = ref<string | null>(null);
  const accessToken = ref<string | null>(null);

  const errorRecoverPassword = ref<boolean | false>(false);
  const isRequestRecoverPassword = ref<boolean | false>(false);

  const errorResetPassword = ref<boolean | false>(false);
  const isRequestResetPassword = ref<boolean | false>(false);

  const errorUser = ref<boolean | false>(false);
  const isRequestUser = ref<boolean | false>(false);

  const login = async (body: any) => {
    error.value = null;

    try {
      isRequestUser.value = true;
      errorUser.value = false;
      // Example API call for login
      const response = await fetch(`${API_URL}signin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: body.email,
          password: body.password,
        }),
      });

      if (!response.ok) {
        Toast.fire({
          title: i18n.t("validation.unlogin"),
          icon: "error",
        });
        isRequestUser.value = false;
        return false;

        //throw new Error('Login failed');
      }

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem("user", JSON.stringify(data));
        localStorage.setItem("authToken", data?.accessToken);
        userUUID.value = data?.data?.uuid || "";
        localStorage.setItem("selectedUserUUID", userUUID.value || "");
        loggedIn.value = true;
        if (data?.data?.roles?.includes("super-admin")) {
          router.push({ name: "gymya-clubs" }).then(() => {
            location.reload();
          });
        } else if (
          data?.data?.roles?.includes("admin") ||
          data?.data?.roles?.includes("assistance")
        ) {
          router
            .push({ name: "list-clubs", params: { userUUID: userUUID.value } })
            .then(() => {
              location.reload();
            });
        }
      } else {
        Toast.fire({
          title: i18n.t("validation.unlogin"),
          icon: "error",
        });
        isRequestUser.value = false;
        return false;
      }
      isRequestUser.value = false;
      // Handle successful login (e.g., store token, redirect)
    } catch (err: any) {
      error.value = err.message;
      errorUser.value = true;
      Toast.fire({
        title: i18n.t("validation.unlogin"),
        icon: "error",
      });
      isRequestUser.value = false;
    }
  };

  const signup = async (body: any) => {
    error.value = null;

    try {
      isRequestUser.value = true;
      errorUser.value = false;
      // Example API call for login
      const response = await fetch(`${API_URL}signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName: body.firstName,
          lastName: body.lastName,
          email: body.email,
          phone: body.phone,
          password: body.password,
          password_confirmation: body.password_confirmation,
        }),
      });

      if (!response.ok) {
        Toast.fire({
          title: "Login failed",
          icon: "error",
        });
        isRequestUser.value = false;
        return false;
      }

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem("user", JSON.stringify(data));
        localStorage.setItem("authToken", data?.accessToken);
        userUUID.value = data?.data?.uuid || "";
        localStorage.setItem("selectedUserUUID", userUUID.value || "");
        loggedIn.value = true;
        if (data?.data?.roles?.includes("super-admin")) {
          router.push({ name: "gymya-clubs" }).then(() => {
            location.reload();
          });
        } else if (data?.data?.roles?.includes("admin")) {
          router
            .push({ name: "list-clubs", params: { userUUID: userUUID.value } })
            .then(() => {
              location.reload();
            });
        }
      } else {
        Toast.fire({
          title: "Token not found in response",
          icon: "error",
        });
        isRequestUser.value = false;
        return false;
      }
      isRequestUser.value = false;
      // Handle successful login (e.g., store token, redirect)
    } catch (err: any) {
      error.value = err.message;
      errorUser.value = true;
      Toast.fire({
        title: "Token not found in response",
        icon: "error",
      });
      isRequestUser.value = false;
    }
  };

  const logout = async () => {
    error.value = null;
    try {
      loggedIn.value = false;
      localStorage.removeItem("user");
      localStorage.removeItem("authToken");
      localStorage.removeItem("selectedUserUUID");
      localStorage.removeItem("selectedClub");
      userUUID.value = null;
      clubUUID.value = null;
      initialValuesClub(null);
      router.push({ name: "login" });

      // Handle successful login (e.g., store token, redirect)
    } catch (err: any) {
      error.value = err.message;
    }
  };

  const recoverPassword = async (body: any) => {
    error.value = null;

    try {
      isRequestRecoverPassword.value = true;
      errorRecoverPassword.value = false;
      // Example API call for login
      const response = await fetch(`${API_URL}password/recover`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: body.email,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        Toast.fire({
          title: i18n.t("message.toast.invalidExtention." + data?.message),
          icon: "error",
        });
      }

      if (response.ok) {
        Toast.fire({
          title: i18n.t("message.toast." + data?.message + ".title"),
          icon: "success",
        });
      } else {
        Toast.fire({
          title: i18n.t("message.toast.invalidExtention." + data?.message),
          icon: "error",
        });
      }
      isRequestRecoverPassword.value = false;
      // Handle successful login (e.g., store token, redirect)
    } catch (err: any) {
      console.log(err);
      errorRecoverPassword.value = true;
      isRequestRecoverPassword.value = false;
    }
  };

  const resetPassword = async (body: any) => {
    error.value = null;

    try {
      isRequestResetPassword.value = true;
      errorResetPassword.value = false;
      // Example API call for login
      const response = await fetch(`${API_URL}password/reset`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token: body.token,
          password: body.password,
          password_confirmation: body.password_confirmation,
        }),
      });

      if (!response.ok) {
        throw new Error("Login failed");
      }

      const data = await response.json();

      if (response) {
        Toast.fire({
          title: i18n.t("message.toast." + data?.message + ".title"),
          icon: "success",
        });
        router.push({ name: "login" }).then(() => {
          location.reload();
        });
      }
      isRequestResetPassword.value = false;
      // Handle successful login (e.g., store token, redirect)
    } catch (err: any) {
      errorResetPassword.value = true;
      isRequestResetPassword.value = false;
      Toast.fire({
        title: "error",
        icon: "error",
      });
    }
  };

  return {
    isRequestUser,
    errorRecoverPassword,
    isRequestRecoverPassword,
    errorResetPassword,
    isRequestResetPassword,
    resetPassword,
    login,
    logout,
    signup,
    recoverPassword,
    error,
  };
}
